import React, { useState, useEffect } from 'react';
import {
  User, MapPin, Mail, Phone, CheckCircle, XCircle, Clock, Heart, Users, Coins, Gift, ArrowDownCircle, ArrowUpCircle, GalleryHorizontal, Star, Zap, Crown, Calendar, Eye, MoreVertical, Trash2, Download, Search
} from 'lucide-react';
import { useParams } from 'react-router-dom';

// Dummy user data (from your seed)
const userSeed = {
  id: 1,
  uuid: 'uuid-1234',
  phone_number: '+2348123456789',
  email: 'john.doe@example.com',
  is_phone_verified: true,
  is_email_verified: true,
  first_name: 'John',
  last_name: 'Doe',
  date_of_birth: '1990-05-15',
  gender: 'male',
  looking_for: 'female',
  show_gender_on_profile: true,
  interests: ["Technology", "Travel", "Music", "Sports"],
  temperament: 'Choleric',
  temperament_description: 'Ambitious and leader-like, often taking charge in situations.',
  temperament_subsidiaries: ["Ambitious", "Leader-like", "Decisive"],
  marriage_readiness: 'ASAP',
  is_in_wedding_zone: false,
  is_subscribed_to_wedding_zone: false,
  current_wedding_zone_plan: {
    name: 'Wedding Zone',
    validity: true,
    start_at: '2025-01-04T14:38:00.000Z',
    ends_at: '2025-03-04T14:38:00.000Z',
  },
  current_subscription_plan: {
    name: 'Gold Plan',
    validity: true,
    start_at: '2025-01-04T14:38:00.000Z',
    ends_at: '2025-01-05T14:38:00.000Z',
  },
  lifestyles: ["Active", "Social", "Adventurous"],
  bio: 'Software engineer passionate about technology and innovation',
  occupation: 'Software Engineer',
  genotype: 'AA',
  education: 'MS in Computer Science',
  profile_photo: 'https://randomuser.me/api/portraits/men/32.jpg',
  gallery: [
    'https://randomuser.me/api/portraits/men/33.jpg',
    'https://randomuser.me/api/portraits/men/34.jpg',
    'https://randomuser.me/api/portraits/men/35.jpg'
  ],
  current_location_lat: "6.5244",
  current_location_long: "3.3792",
  current_location_city: "Trans Ekulu",
  current_location_locality: "Enugu",
  current_location_name: "Trans Ekulu, Enugu",
  current_location_address: "12 Abakaliki Onitsha Express way, Trans Ekulu, Enugu, Nigeria",
  current_location_country: "Nigeria",
  has_agreed_terms: true,
  is_profile_complete: true,
  last_active: new Date().toISOString(),
  is_online: true,
  account_type: 'user',
  status: 'active',
  isSuperAdmin: false,
  referral_code: 'REF12345',
  referred_by: 2,
  payment_platform: 'Paystack',
  bank_account_number: '1234567890',
  bank_account_name: 'John Doe',
  bank_name: 'First Bank',
  bank_code: '011',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  // Dummy stats
  followers: 120,
  following: 75,
  matches: 34,
  coins_available: 1200,
  coins_bought: 5000,
  coins_gifted: 800,
  coins_withdrawn: 300,
};

// Dummy coin transactions
const coinTransactions = Array.from({ length: 40 }, (_, i) => ({
  id: i + 1,
  type: i % 3 === 0 ? 'buy' : i % 3 === 1 ? 'gift' : 'withdraw',
  amount: 1000 + i * 100,
  date: `2024-04-${(i % 28) + 1}`,
  status: i % 2 === 0 ? 'completed' : 'pending',
  details: i % 3 === 0 ? 'Bought via card' : i % 3 === 1 ? 'Gifted to user' : 'Withdrawal request',
}));

// Dummy subscription history
const subscriptionHistory = Array.from({ length: 25 }, (_, i) => ({
  id: i + 1,
  plan: i % 3 === 0 ? 'Gold Plan' : i % 3 === 1 ? 'Premium Plan' : 'Basic Plan',
  amount: 29.99 + (i * 5),
  paymentMethod: i % 2 === 0 ? 'Credit Card' : 'PayPal',
  startDate: `2024-0${(i % 3) + 1}-${(i % 28) + 1}`,
  endDate: `2024-0${(i % 3) + 2}-${(i % 28) + 1}`,
  status: i % 4 === 0 ? 'active' : i % 4 === 1 ? 'expired' : 'pending',
}));

const SingleUser = () => {
  const { id } = useParams();
  const user = userSeed; // In real app, fetch by id

  // Tab state
  const [tab, setTab] = useState('coins');

  // Coin transactions state
  const [coinSearchQuery, setCoinSearchQuery] = useState('');
  const [coinTypeFilter, setCoinTypeFilter] = useState('all');
  const [coinItemsPerPage, setCoinItemsPerPage] = useState(10);
  const [coinCurrentPage, setCoinCurrentPage] = useState(1);

  // Subscription history state
  const [subSearchQuery, setSubSearchQuery] = useState('');
  const [subPlanFilter, setSubPlanFilter] = useState('all');
  const [subStatusFilter, setSubStatusFilter] = useState('all');
  const [subItemsPerPage, setSubItemsPerPage] = useState(10);
  const [subCurrentPage, setSubCurrentPage] = useState(1);

  // Helper for status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
      case 'completed':
        return 'text-green-600 dark:text-green-400';
      case 'expired':
        return 'text-red-600 dark:text-red-400';
      case 'pending':
        return 'text-yellow-600 dark:text-yellow-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  // Coin transactions filtering and pagination
  const filteredCoinTransactions = coinTransactions.filter((tx) => {
    if (coinTypeFilter !== 'all' && tx.type !== coinTypeFilter) return false;
    const q = coinSearchQuery.toLowerCase();
    if (
      !(
        tx.type.toLowerCase().includes(q) ||
        String(tx.amount).includes(q) ||
        tx.details.toLowerCase().includes(q)
      )
    ) {
      return false;
    }
    return true;
  });

  const coinTotalItems = filteredCoinTransactions.length;
  const coinTotalPages = Math.ceil(coinTotalItems / coinItemsPerPage);
  const paginatedCoinTransactions = filteredCoinTransactions.slice(
    (coinCurrentPage - 1) * coinItemsPerPage,
    coinCurrentPage * coinItemsPerPage
  );

  // Subscription history filtering and pagination
  const filteredSubscriptionHistory = subscriptionHistory.filter((sub) => {
    if (subPlanFilter !== 'all' && sub.plan !== subPlanFilter) return false;
    if (subStatusFilter !== 'all' && sub.status !== subStatusFilter) return false;
    const q = subSearchQuery.toLowerCase();
    if (
      !(
        sub.plan.toLowerCase().includes(q) ||
        sub.paymentMethod.toLowerCase().includes(q)
      )
    ) {
      return false;
    }
    return true;
  });

  const subTotalItems = filteredSubscriptionHistory.length;
  const subTotalPages = Math.ceil(subTotalItems / subItemsPerPage);
  const paginatedSubscriptionHistory = filteredSubscriptionHistory.slice(
    (subCurrentPage - 1) * subItemsPerPage,
    subCurrentPage * subItemsPerPage
  );

  // Handle page changes
  const goToCoinPage = (page) => {
    if (page < 1 || page > coinTotalPages) return;
    setCoinCurrentPage(page);
  };

  const goToSubPage = (page) => {
    if (page < 1 || page > subTotalPages) return;
    setSubCurrentPage(page);
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCoinCurrentPage(1);
  }, [coinTypeFilter, coinSearchQuery, coinItemsPerPage]);

  useEffect(() => {
    setSubCurrentPage(1);
  }, [subPlanFilter, subStatusFilter, subSearchQuery, subItemsPerPage]);

  return (
    <div className="space-y-8">
      {/* Profile Header */}
      <div className="flex flex-col md:flex-row md:items-center gap-8 bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col items-center md:items-start">
          <img src={user.profile_photo} alt="Profile" className="w-32 h-32 rounded-full object-cover border-4 border-pink-500" />
          <div className="flex gap-2 mt-3">
            <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${user.is_online ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'}`}>
              {user.is_online ? 'Online' : 'Offline'}
            </span>
            <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
              {user.status}
            </span>
          </div>
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{user.first_name} {user.last_name}</h2>
            <span className="text-sm text-gray-500 dark:text-gray-400">({user.gender}, {user.date_of_birth})</span>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-gray-600 dark:text-gray-400">
            <Mail className="w-4 h-4" /> {user.email}
            <Phone className="w-4 h-4 ml-4" /> {user.phone_number}
            <MapPin className="w-4 h-4 ml-4" /> {user.current_location_city}, {user.current_location_country}
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            <span className="text-xs bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400 px-2 py-1 rounded">{user.occupation}</span>
            <span className="text-xs bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400 px-2 py-1 rounded">{user.education}</span>
            <span className="text-xs bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400 px-2 py-1 rounded">Genotype: {user.genotype}</span>
          </div>
          <div className="mt-2 text-gray-700 dark:text-gray-300">{user.bio}</div>
        </div>
        {/* Gallery */}
        <div className="flex flex-col items-center gap-2">
          <GalleryHorizontal className="w-6 h-6 text-pink-500 mb-1" />
          <div className="flex gap-2">
            {user.gallery.map((img, i) => (
              <img key={i} src={img} alt="Gallery" className="w-12 h-12 rounded object-cover border border-gray-200 dark:border-gray-700" />
            ))}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col items-center">
          <Users className="w-6 h-6 text-blue-500 mb-1" />
          <div className="text-lg font-bold text-gray-900 dark:text-white">{user.followers}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Followers</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col items-center">
          <Users className="w-6 h-6 text-green-500 mb-1" />
          <div className="text-lg font-bold text-gray-900 dark:text-white">{user.following}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Following</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col items-center">
          <Heart className="w-6 h-6 text-pink-500 mb-1" />
          <div className="text-lg font-bold text-gray-900 dark:text-white">{user.matches}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Matches</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col items-center">
          <Coins className="w-6 h-6 text-yellow-500 mb-1" />
          <div className="text-lg font-bold text-gray-900 dark:text-white">{user.coins_available}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Coins Available</div>
        </div>
      </div>

      {/* Coin Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col items-center">
          <ArrowDownCircle className="w-6 h-6 text-green-500 mb-1" />
          <div className="text-lg font-bold text-gray-900 dark:text-white">{user.coins_bought}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Coins Bought</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col items-center">
          <Gift className="w-6 h-6 text-yellow-500 mb-1" />
          <div className="text-lg font-bold text-gray-900 dark:text-white">{user.coins_gifted}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Coins Gifted</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col items-center">
          <ArrowUpCircle className="w-6 h-6 text-red-500 mb-1" />
          <div className="text-lg font-bold text-gray-900 dark:text-white">{user.coins_withdrawn}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Coins Withdrawn</div>
        </div>
      </div>

      {/* Info & Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 space-y-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Personal Info</h3>
          <div className="flex flex-wrap gap-2">
            <span className="text-xs bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200 px-2 py-1 rounded">Gender: {user.gender}</span>
            <span className="text-xs bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200 px-2 py-1 rounded">Looking for: {user.looking_for}</span>
            <span className="text-xs bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200 px-2 py-1 rounded">Marriage Readiness: {user.marriage_readiness}</span>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {user.interests.map((interest, i) => (
              <span key={i} className="text-xs bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 px-2 py-1 rounded">{interest}</span>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {user.lifestyles.map((life, i) => (
              <span key={i} className="text-xs bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400 px-2 py-1 rounded">{life}</span>
            ))}
          </div>
          <div className="mt-2">
            <span className="font-semibold text-gray-900 dark:text-white">Temperament:</span> {user.temperament}
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{user.temperament_description}</div>
            <div className="flex flex-wrap gap-2">
              {user.temperament_subsidiaries.map((t, i) => (
                <span key={i} className="text-xs bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400 px-2 py-1 rounded">{t}</span>
              ))}
            </div>
          </div>
        </div>
        {/* Location & Account */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 space-y-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Location & Account</h3>
          <div className="text-sm text-gray-700 dark:text-gray-300">{user.current_location_address}</div>
          <div className="flex flex-wrap gap-2 mt-2">
            <span className="text-xs bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200 px-2 py-1 rounded">Account Type: {user.account_type}</span>
            <span className="text-xs bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200 px-2 py-1 rounded">Referral: {user.referral_code}</span>
            <span className="text-xs bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200 px-2 py-1 rounded">Payment: {user.payment_platform}</span>
          </div>
          <div className="mt-2">
            <span className="font-semibold text-gray-900 dark:text-white">Bank:</span> {user.bank_name} ({user.bank_account_number})
          </div>
        </div>
      </div>

      {/* Plans */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Current Subscription</h3>
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500" />
            <span className="font-medium text-gray-900 dark:text-white">{user.current_subscription_plan.name}</span>
            <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 ml-2">Active</span>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{user.current_subscription_plan.start_at} - {user.current_subscription_plan.ends_at}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Wedding Zone</h3>
          <div className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-purple-500" />
            <span className="font-medium text-gray-900 dark:text-white">{user.current_wedding_zone_plan.name}</span>
            <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 ml-2">Active</span>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{user.current_wedding_zone_plan.start_at} - {user.current_wedding_zone_plan.ends_at}</div>
        </div>
      </div>

      {/* Tabular Info */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700 px-6 pt-6">
          <button
            className={`px-4 py-2 rounded-t-lg font-medium text-sm focus:outline-none ${tab === 'coins' ? 'bg-pink-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
            onClick={() => setTab('coins')}
          >
            Coin Transactions
          </button>
          <button
            className={`px-4 py-2 rounded-t-lg font-medium text-sm focus:outline-none ${tab === 'subs' ? 'bg-pink-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
            onClick={() => setTab('subs')}
          >
            Subscription History
          </button>
        </div>
        <div className="p-6">
          {tab === 'coins' ? (
            <div>
              {/* Coin Transactions Filters */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search coin transactions..."
                    value={coinSearchQuery}
                    onChange={e => setCoinSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  />
                </div>
                <select
                  value={coinTypeFilter}
                  onChange={e => setCoinTypeFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                >
                  <option value="all">All Types</option>
                  <option value="buy">Buy</option>
                  <option value="gift">Gift</option>
                  <option value="withdraw">Withdraw</option>
                </select>
                <select
                  value={coinItemsPerPage}
                  onChange={e => setCoinItemsPerPage(Number(e.target.value))}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                >
                  {[10, 20, 30, 50, 100].map(limit => (
                    <option key={limit} value={limit}>{limit} per page</option>
                  ))}
                </select>
              </div>

              {/* Coin Transactions Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Details</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {paginatedCoinTransactions.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">No coin transactions found.</td>
                      </tr>
                    ) : (
                      paginatedCoinTransactions.map((tx) => (
                        <tr key={tx.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-6 py-4 whitespace-nowrap capitalize">{tx.type}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{tx.amount}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{tx.date}</td>
                          <td className={`px-6 py-4 whitespace-nowrap ${getStatusColor(tx.status)}`}>{tx.status}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{tx.details}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Coin Transactions Pagination */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 mt-6">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Showing {(coinCurrentPage - 1) * coinItemsPerPage + 1}
                  -{Math.min(coinCurrentPage * coinItemsPerPage, coinTotalItems)} of {coinTotalItems} coin transactions
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                    onClick={() => goToCoinPage(coinCurrentPage - 1)}
                    disabled={coinCurrentPage === 1}
                  >
                    Prev
                  </button>
                  {Array.from({ length: coinTotalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      className={`px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                        coinCurrentPage === page ? 'bg-pink-600 text-white dark:bg-pink-700' : ''
                      }`}
                      onClick={() => goToCoinPage(page)}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                    onClick={() => goToCoinPage(coinCurrentPage + 1)}
                    disabled={coinCurrentPage === coinTotalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div>
              {/* Subscription History Filters */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search subscriptions..."
                    value={subSearchQuery}
                    onChange={e => setSubSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  />
                </div>
                <select
                  value={subPlanFilter}
                  onChange={e => setSubPlanFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                >
                  <option value="all">All Plans</option>
                  <option value="Gold Plan">Gold Plan</option>
                  <option value="Premium Plan">Premium Plan</option>
                  <option value="Basic Plan">Basic Plan</option>
                </select>
                <select
                  value={subStatusFilter}
                  onChange={e => setSubStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="expired">Expired</option>
                  <option value="pending">Pending</option>
                </select>
                <select
                  value={subItemsPerPage}
                  onChange={e => setSubItemsPerPage(Number(e.target.value))}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                >
                  {[10, 20, 30, 50, 100].map(limit => (
                    <option key={limit} value={limit}>{limit} per page</option>
                  ))}
                </select>
              </div>

              {/* Subscription History Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Plan</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Payment Method</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Start Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">End Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {paginatedSubscriptionHistory.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">No subscriptions found.</td>
                      </tr>
                    ) : (
                      paginatedSubscriptionHistory.map((sub) => (
                        <tr key={sub.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-6 py-4 whitespace-nowrap">{sub.plan}</td>
                          <td className="px-6 py-4 whitespace-nowrap">${sub.amount}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{sub.paymentMethod}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{sub.startDate}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{sub.endDate}</td>
                          <td className={`px-6 py-4 whitespace-nowrap ${getStatusColor(sub.status)}`}>{sub.status}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Subscription History Pagination */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 mt-6">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Showing {(subCurrentPage - 1) * subItemsPerPage + 1}
                  -{Math.min(subCurrentPage * subItemsPerPage, subTotalItems)} of {subTotalItems} subscriptions
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                    onClick={() => goToSubPage(subCurrentPage - 1)}
                    disabled={subCurrentPage === 1}
                  >
                    Prev
                  </button>
                  {Array.from({ length: subTotalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      className={`px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                        subCurrentPage === page ? 'bg-pink-600 text-white dark:bg-pink-700' : ''
                      }`}
                      onClick={() => goToSubPage(page)}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                    onClick={() => goToSubPage(subCurrentPage + 1)}
                    disabled={subCurrentPage === subTotalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SingleUser;
